import { ComponentPortal } from '@angular/cdk/portal';
import { NgStyle } from '@angular/common';
import {
  Injectable,
  Component,
  Injector,
  InjectionToken,
  ViewContainerRef,
  ComponentRef,
  Type,
} from '@angular/core';
import { MatDrawer } from '@angular/material/sidenav';
import { BehaviorSubject, Observable, Subject } from 'rxjs';

export enum DrawerCommand {
  Open = 'open',
  Close = 'close',
}

export const DRAWER_DATA_INJECTION_TOKEN = new InjectionToken<string>(
  'DRAWER_DATA_INJECTION_TOKEN',
);

export interface DrawerConfig {
  style?: Record<string, string>;
  disableClose?: boolean;
}

export interface DrawerData {
  data: Record<string, unknown> & {
    onDiscard?: () => void;
    onClose?: () => void;
  };
  config: DrawerConfig;
}

@Injectable({
  providedIn: 'root',
})
export class DrawerFacadeService {
  private drawer!: MatDrawer;
  private viewContainerRef!: ViewContainerRef;
  private drawerSubject = new Subject<DrawerConfig>();

  setDrawer(drawer: MatDrawer): Observable<DrawerConfig> {
    this.drawer = drawer;
    return this.drawerSubject;
  }

  setViewContainerRef(viewContainerRef: ViewContainerRef): void {
    this.viewContainerRef = viewContainerRef;
  }

  open<T>(component: Type<T>, { data, config }: DrawerData): ComponentRef<T> {
    if (!this.drawer || !this.viewContainerRef) {
      throw new Error('Drawer or ViewContainerRef is not set!');
    }

    // Clear previous content
    this.viewContainerRef.clear();

    // Create and render the component dynamically
    const componentRef = this.viewContainerRef.createComponent(component);

    this.drawerSubject.next(config);
    // Pass data to the component instance
    if (data) {
      Object.assign(componentRef.instance, { data });
    }
    this.drawer.disableClose = !!config.disableClose;
    this.drawer.open();
    this.drawer.closedStart.subscribe(() => {
      data?.onDiscard?.();
      data?.onClose?.();
    });
    return componentRef;
  }

  close(): void {
    this.drawer?.close();
  }
}
